import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import {Box, ButtonBase, CircularProgress, Link, Stack, Typography} from "@mui/material";
import {CloudinaryService} from "crqlar-react-common/dist/services/cloudinary.service";
import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {firstFontName, isFontFileName, ownFontFamily} from "../fonts";

const MAX_FONT_BYTES = 2 * 1024 * 1024;

/** Whether the browser can use [file] as a font; without the FontFace API it can't tell, so yes. */
const readableFont = async (file: File): Promise<boolean> => {
    if (typeof FontFace === 'undefined') return true;
    try {
        await new FontFace('crqlar-font-check', await file.arrayBuffer()).load();
        return true;
    } catch {
        return false;
    }
};

interface Props {
    fontUrl?: string | null
    fontFamily?: string | null
    /** The host's own message, e.g. that the file is still missing. */
    error?: string
    disabled?: boolean
    onUploaded: (font: {fontUrl: string, fontFamily: string}) => void
    onRemove: () => void
}

/**
 * The own font's one file, uploaded like the logo: the page loads the file itself, named after it.
 * Titles use the file's own weight, larger - no second file for bold.
 */
const FontFileUpload = ({fontUrl, fontFamily, error, disabled, onUploaded, onRemove}: Props) => {
    const {t} = useTranslation();
    const [uploading, setUploading] = useState(false);
    const [fileError, setFileError] = useState<string>();
    const name = fontUrl ? firstFontName(fontFamily) : undefined;
    const shownError = fileError ?? error;

    const upload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) return;

        setFileError(undefined);
        if (!isFontFileName(file.name)) return setFileError(t('This isn\'t a font file. Use WOFF2, WOFF, TTF or OTF.'));
        if (file.size > MAX_FONT_BYTES) return setFileError(t('The font file is larger than 2 MB.'));
        if (!await readableFont(file)) return setFileError(t('This font file can\'t be read.'));

        setUploading(true);
        try {
            // Saved as an own font, the link loads as a font file whatever it looks like: https is all it needs
            const url: unknown = (await CloudinaryService.uploadFile(file))?.secure_url;
            if (typeof url !== 'string' || !/^https:\/\//i.test(url)) throw new Error('No https link in the upload response');
            onUploaded({fontUrl: url, fontFamily: ownFontFamily(file.name)});
        } catch {
            setFileError(t('The font file couldn\'t be uploaded.'));
        } finally {
            setUploading(false);
        }
    };

    const frameSx = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        width: '100%',
        minHeight: 60,
        px: 2,
        py: 1.25,
        border: '1px solid',
        borderRadius: 1,
        textAlign: 'left',
    } as const;

    return (
        <Stack gap={0.5} width={'100%'}>
            {name ? (
                <Box sx={{...frameSx, borderColor: 'divider'}}>
                    <Stack minWidth={0}>
                        <Typography noWrap sx={{fontFamily: fontFamily ?? undefined, fontSize: 18, lineHeight: 1.4}}>{name}</Typography>
                        {!disabled && (
                            <Link component={'button'} type={'button'} variant={'body2'} color={'text.secondary'} onClick={onRemove} sx={{alignSelf: 'flex-start'}}>
                                {t('Remove')}
                            </Link>
                        )}
                    </Stack>
                    <CheckCircleOutlineRoundedIcon color={'success'}/>
                </Box>
            ) : (
                <ButtonBase
                    component={'label'}
                    disabled={disabled || uploading}
                    sx={{
                        ...frameSx,
                        borderStyle: 'dashed',
                        borderColor: shownError ? 'error.main' : 'text.secondary',
                        '&:hover': {bgcolor: 'action.hover'},
                        '&.Mui-focusVisible': {borderColor: 'primary.main', borderStyle: 'solid'},
                    }}
                >
                    <Stack minWidth={0}>
                        <Typography variant={'body1'} sx={{fontWeight: 500}}>
                            {uploading ? t('Uploading…') : t('Upload font file')}
                        </Typography>
                        <Typography variant={'body2'} color={'text.secondary'}>
                            {t('WOFF2, WOFF, TTF or OTF · up to 2 MB')}
                        </Typography>
                    </Stack>
                    {uploading ? <CircularProgress size={20}/> : <FileUploadOutlinedIcon color={'action'}/>}
                    <input type={'file'} hidden accept={'.woff2,.woff,.ttf,.otf'} aria-label={t('Upload font file')} onChange={upload}/>
                </ButtonBase>
            )}
            {!!shownError && <Typography variant={'body2'} color={'error'}>{shownError}</Typography>}
        </Stack>
    );
};

export default FontFileUpload;
